import React, { useCallback, useMemo, useEffect } from 'react'

import { useAIMap } from '../../ai/hooks'
import { useAILayers } from '../../aiLayers/hooks'
import { useEsri } from '../hooks'

import { BASE_URL, ICONS } from '../../constants'

import GraphicLayer from './GraphicLayer'

const getAISymbol = (geometryType, isSelected) => (icon) => {
  if (geometryType === 'polygon') {
    return {
      type: 'simple-fill', // autocasts as new SimpleFillSymbol()
      color: [51, 51, 204, 0.9],
      style: 'solid',
      outline: {
        // autocasts as new SimpleLineSymbol()
        color: 'white',
        width: 1,
      },
    }
  } else {
    const size = '30px'
    const iconPath = `${BASE_URL}${
      isSelected ? ICONS.AI.ACTIVE.DIR : ICONS.AI.DIR
    }`

    return {
      type: 'picture-marker',
      url: `${iconPath}${icon}`,
      width: size,
      height: size,
    }
  }
}

const AILayer = ({ map, view }) => {
  const [{ ais, selected, visibleLayerIds }, { select, clear }] = useAIMap()
  const aiLayers = useAILayers()
  const [ready, { Polygon }] = useEsri(['esri/geometry/Polygon'])

  const layersById = useMemo(() => {
    let byId = {}
    aiLayers.forEach((layer) => (byId[layer.id] = layer))
    return byId
  }, [aiLayers])
  const getLayerIcon = useCallback(
    (layerId) => {
      const layer = layersById[layerId]
      return (layer && layer.icon) || 'question.png'
    },
    [layersById]
  )
  const getLayerGeometryType = useCallback(
    (layerId) => {
      const layer = layersById[layerId]
      return (layer && layer.geometryType) || 'point'
    },
    [layersById]
  )

  const getLayerPopupTemplate = useCallback(
    (layerId) => {
      const layer = layersById[layerId]
      return layer && layer.popupTemplate
    },
    [layersById]
  )

  const graphics = useMemo(
    () =>
      ais
        .filter((ai) => ai)
        .filter((ai) => visibleLayerIds.includes(ai.layerId))
        .map((ai) => {
          const isSelected =
            selected.ids.length && selected.ids[selected.current] === ai.id
          const geometry = ai.geometry
          geometry.type = getLayerGeometryType(ai.layerId)
          const popupTemplateString = getLayerPopupTemplate(ai.layerId)
          const popupTemplate = popupTemplateString
            ? JSON.parse(popupTemplateString)
            : undefined
          return {
            geometry,
            symbol: getAISymbol(
              geometry.type,
              isSelected
            )(getLayerIcon(ai.layerId)),
            attributes: ai.attributes,
            popupTemplate,
          }
        }),
    [
      ais,
      selected,
      getLayerIcon,
      getLayerGeometryType,
      visibleLayerIds,
      getLayerPopupTemplate,
    ]
  )

  const onSelect = useCallback(
    (graphics) => select(graphics.map((g) => g.attributes.id)),
    [select]
  )

  // Zoom/pan on selection change
  useEffect(() => {
    if (ready && selected.ids.length) {
      const aiId = selected.ids[selected.current]
      const graphic = graphics.find((g) => g.attributes.id === aiId)
      if (graphic && graphic.popupTemplate) {
        view.popup &&
          view.popup.open({
            //location: graphic.geometry,
            features: [{ ...graphic }],
          })
      }
      if (graphic && graphic.geometry) {
        const zoom = view.zoom > 15 ? view.zoom : 15
        if (graphic.geometry.x && graphic.geometry.y) {
          view.goTo({
            center: [graphic.geometry.x, graphic.geometry.y],
            zoom,
          })
        } else {
          view.goTo({
            target: new Polygon({
              type: 'polygon',
              rings: graphic.geometry.rings,
              spatialReference: { wkid: 4326 },
            }),
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, view, ready, Polygon]) // only update when selection changes

  return (
    <GraphicLayer
      id="AILayer"
      map={map}
      view={view}
      graphics={graphics}
      onSelect={onSelect}
      onDeselect={clear}
    />
  )
}

export default AILayer
